import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, Stack, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Card, CardContent, Typography, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { CompactPicker } from 'react-color';


// 새로운 타입 정의
interface SizeRow { sizeName: string; quantity: number; }
interface ColorGroup { colorName: string; colorCode: string; sizes: SizeRow[]; }

// 프리셋 컬러 팔레트
const COLOR_PRESETS = [
  '#000000', '#ffffff', '#e53935', '#fbc02d', '#43a047', '#1e88e5', '#8e24aa', '#ff9800',
  '#795548', '#cfd8dc', '#bdbdbd', '#607d8b'
];

export function OptionEditor({
  options, setOptions
}: {
  options: ColorGroup[];
  setOptions: (opts: ColorGroup[]) => void;
}) {
  // 컬러 그룹 추가
  const handleAddColor = () => {
    setOptions([
      ...options,
      { colorName: '', colorCode: '#000000', sizes: [{ sizeName: '', quantity: 1 }] }
    ]);
  };
  // 컬러 그룹 삭제
  const handleRemoveColor = (colorIdx: number) => {
    if (options.length <= 1) {
      alert('최소 1개의 옵션은 필요합니다.');
      return;
    }
    setOptions(options.filter((_, i) => i !== colorIdx));
  };
  // 컬러명/컬러코드 변경
  const handleColorChange = (colorIdx: number, field: 'colorName' | 'colorCode', value: string) => {
    setOptions(options.map((group, i) =>
      i === colorIdx ? { ...group, [field]: value } : group
    ));
  };
  // 사이즈 row 추가
  const handleAddSize = (colorIdx: number) => {
    setOptions(options.map((group, i) =>
      i === colorIdx ? { ...group, sizes: [...group.sizes, { sizeName: '', quantity: 1 }] } : group
    ));
  };
  // 사이즈 row 삭제
  const handleRemoveSize = (colorIdx: number, sizeIdx: number) => {
    if (options[colorIdx].sizes.length <= 1) {
      alert('최소 1개의 사이즈는 필요합니다.');
      return;
    }
    setOptions(options.map((group, i) =>
      i === colorIdx ? { ...group, sizes: group.sizes.filter((_, j) => j !== sizeIdx) } : group
    ));
  };
  // 사이즈명/수량 변경
  const handleSizeChange = (colorIdx: number, sizeIdx: number, field: 'sizeName' | 'quantity', value: any) => {
    setOptions(options.map((group, i) =>
      i === colorIdx ? {
        ...group,
        sizes: group.sizes.map((row, j) =>
          j === sizeIdx
            ? {
                ...row,
                [field]: field === 'quantity'
                  ? value.replace(/[^0-9]/g, '') // 숫자만 허용, string으로 저장
                  : value
              }
            : row
        )
      } : group
    ));
  };

  const [openColorPickerIdx, setOpenColorPickerIdx] = useState<number | null>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (openColorPickerIdx !== null && colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setOpenColorPickerIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClick, { passive: true });
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openColorPickerIdx]);

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="h6" mb={2} fontWeight={700}>상품 옵션</Typography>
      <Stack spacing={3}>
        {options.map((group, colorIdx) => (
          <Card key={colorIdx} sx={{ bgcolor: '#f9fafd', borderRadius: 3, boxShadow: 1, p: 1, minHeight : 360}}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <TextField
                  label="컬러명"
                  value={group.colorName}
                  onChange={e => handleColorChange(colorIdx, 'colorName', e.target.value)}
                  sx={{ minWidth: 150, bgcolor: '#fff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  placeholder="예: 블랙"
                />
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '50%',
                      bgcolor: group.colorCode,
                      border: '2px solid #e0e0e0',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                    onClick={() => setOpenColorPickerIdx(openColorPickerIdx === colorIdx ? null : colorIdx)}
                  />
                  {openColorPickerIdx === colorIdx && (
                    <Box
                      ref={colorPickerRef}
                      sx={{
                        position: 'absolute',
                        top: 50,
                        left: 0,
                        zIndex: 10,
                      }}
                    >
                      <CompactPicker
                        color={group.colorCode}
                        onChange={(color: any) => {
                          handleColorChange(colorIdx, 'colorCode', color.hex);
                          // setOpenColorPickerIdx(null); // 선택할 때마다 닫지 않도록 제거
                        }}
                        colors={COLOR_PRESETS}
                        triangle="hide"
                      />
                    </Box>
                  )}
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                {options.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveColor(colorIdx)}
                    sx={{ ml: 'auto', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              <TableContainer component={Paper} sx={{ boxShadow: 0, borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f5f5f5' } }}>
                      <TableCell align="center">사이즈명</TableCell>
                      <TableCell align="center">수량</TableCell>
                      <TableCell align="center" padding="none"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.sizes.map((row, sizeIdx) => (
                      <TableRow key={sizeIdx}>
                        <TableCell align="center">
                          <TextField
                            value={row.sizeName}
                            onChange={e => handleSizeChange(colorIdx, sizeIdx, 'sizeName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ bgcolor: '#fff', borderRadius: 2, minWidth: 100 }}
                            placeholder="예: S"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            value={row.quantity || ''}
                            onChange={e => handleSizeChange(colorIdx, sizeIdx, 'quantity', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              bgcolor: '#fff', 
                              borderRadius: 2, 
                              minWidth: 80,
                              '& input[type=number]': {
                                MozAppearance: 'textfield', // Firefox 스피너 제거
                              },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none', // Chrome/Safari 스피너 제거
                                margin: 0,
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none', // Chrome/Safari 스피너 제거
                                margin: 0,
                              },
                            }}
                            onWheel={(e) => e.currentTarget.blur()} // 스크롤 시 포커스 해제
                            InputProps={{ 
                              onWheel: (e) => e.preventDefault(), // 휠 이벤트 차단
                            }}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              type: 'text', // 키보드 전용으로 text 타입 사용
                              min: 1
                            }}
                            placeholder="1 이상"
                          />
                        </TableCell>
                        <TableCell align="center" padding="none">
                          {group.sizes.length > 1 && (
                            <IconButton size="small" onClick={() => handleRemoveSize(colorIdx, sizeIdx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => handleAddSize(colorIdx)}
                  sx={{ borderRadius: 2 }}
                >
                  사이즈 추가
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddCircleIcon />}
          onClick={handleAddColor}
          sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
        >
          색상 옵션 추가
        </Button>
      </Box>
    </Box>
  );
} 